import { useEffect } from "react";
import { X, ImageIcon, MessageSquare, Sparkles, Wand2, Layers } from "lucide-react";

/**
 * Large in-app explainer that clarifies the two roles an asset can play:
 *
 *   - **Referencia** (base point): the image the model starts from. In the
 *     chat this comes from the last generated image (memoria del chat), in
 *     the creative editor it's the main image being edited.
 *   - **Apoyo** (supporting cue): text prompts + additional images attached
 *     to refine style, subject details, composition. They guide the result
 *     but do NOT replace the base.
 *
 * Rendered as a modal over the chat. Keep it fully readable on mobile
 * (scroll inside).
 */
export default function HowItWorksModal({ open, onClose }) {
    useEffect(() => {
        if (!open) return;
        const h = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", h);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", h);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            data-testid="how-it-works-modal"
            className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border hairline border-ink-600 bg-ink-900 shadow-glow"
            >
                {/* header */}
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b hairline border-ink-600 flex items-center gap-3 px-6 py-4">
                    <Sparkles className="w-5 h-5 text-periwinkle-500" />
                    <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                            Guía rápida
                        </p>
                        <h2 className="font-display text-2xl text-fog-100 leading-tight">
                            Cómo funciona: Referencia y Apoyo
                        </h2>
                    </div>
                    <button
                        data-testid="how-it-works-close"
                        onClick={onClose}
                        className="w-9 h-9 rounded-full border hairline border-ink-600 text-fog-300 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-6 py-6 space-y-8">
                    {/* two pillars */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <Pillar
                            testId="how-it-works-card-reference"
                            badge="REFERENCIA"
                            badgeTone="peri"
                            icon={<ImageIcon className="w-5 h-5" />}
                            title="Punto base"
                            desc={
                                <>
                                    Es la imagen desde la que partimos. La IA
                                    toma su composición, luz, encuadre y estilo
                                    como estructura y le aplica los cambios que
                                    describas.
                                </>
                            }
                            sources={[
                                "Se traen desde /references — el portafolio de tu marca.",
                                "En el chat se añaden desde el panel Config. En el editor creativo: la imagen abierta es la referencia.",
                            ]}
                        />
                        <Pillar
                            testId="how-it-works-card-apoyo"
                            badge="APOYO"
                            badgeTone="amber"
                            icon={<MessageSquare className="w-5 h-5" />}
                            title="Guía y contexto"
                            desc={
                                <>
                                    Son las señales que complementan el prompt:
                                    textos del chat + imágenes subidas
                                    directamente desde el clip (📎). Le dicen a
                                    la IA <em>qué</em> ajustar sin reemplazar
                                    la base.
                                </>
                            }
                            sources={[
                                "Texto del mensaje en el chat o del prompt del editor.",
                                "Imágenes subidas con el clip (📎) desde tu equipo — no se guardan en el portafolio.",
                            ]}
                        />
                    </div>

                    {/* flow example */}
                    <section>
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-3 flex items-center gap-2">
                            <Wand2 className="w-3 h-3" />
                            Ejemplo: creando una pieza de Toyota
                        </p>
                        <ol className="space-y-3">
                            <Step
                                n="1"
                                title="Sube tu referencia base"
                                body={
                                    <>
                                        Entra a <strong>Referencias</strong> y
                                        sube una pieza antigua de tu marca con
                                        la composición que quieres conservar
                                        (p.&nbsp;ej. una piloto niña con un
                                        auto de juguete y el claim
                                        &ldquo;Ser niño&rdquo;).
                                    </>
                                }
                            />
                            <Step
                                n="2"
                                title="Abre un chat y trae la referencia desde Config"
                                body={
                                    <>
                                        Abre <strong>Nuevo chat</strong>, abre
                                        el panel <strong>Config</strong> y
                                        haz click en{" "}
                                        <em>&ldquo;Traer desde /references&rdquo;</em>.
                                        La pieza cargada se convierte en la{" "}
                                        <strong>referencia base</strong> de
                                        esta conversación.
                                    </>
                                }
                            />
                            <Step
                                n="3"
                                title="Sube apoyos desde el clip (📎)"
                                body={
                                    <>
                                        Con el clip junto al prompt sube
                                        directamente desde tu equipo un par de
                                        imágenes de <strong>apoyo</strong> — p.
                                        ej. una foto del auto nuevo y un
                                        moodboard de paisajes. No se guardan en
                                        /references: son sólo para esta pieza.
                                    </>
                                }
                            />
                            <Step
                                n="4"
                                title="Escribe el cambio y refina con el editor"
                                body={
                                    <>
                                        Prompt: &ldquo;Cambia el escenario por
                                        un camino rocoso al amanecer y sustituye
                                        el auto por el 4Runner del apoyo,
                                        manteniendo la piloto niña y el
                                        claim&rdquo;. Si algo falla, abre la
                                        imagen generada en el editor creativo y
                                        ajusta con otro prompt.
                                    </>
                                }
                            />
                        </ol>
                    </section>

                    {/* models tip */}
                    <section className="rounded-xl border hairline border-periwinkle-500/30 bg-periwinkle-500/5 px-4 py-3 flex gap-3 text-sm text-fog-200">
                        <Layers className="w-4 h-4 text-periwinkle-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-fog-100 mb-1">
                                Usa modelos image-to-image si cargas apoyos
                            </p>
                            <p className="text-fog-300 text-sm leading-relaxed">
                                <strong>Nano Banana Pro · Edit</strong> y{" "}
                                <strong>FLUX Pro Kontext</strong> saben
                                combinar la referencia base con múltiples
                                apoyos. Modelos <em>text-to-image</em> puros
                                ignorarán los apoyos adjuntos.
                            </p>
                        </div>
                    </section>

                    {/* footer */}
                    <p className="text-xs text-fog-500 font-mono pt-2 border-t hairline border-ink-600">
                        Tip · el chat tiene memoria: cada nueva imagen se
                        genera sobre la anterior. Para empezar &ldquo;en
                        blanco&rdquo; crea un <strong>Nuevo chat</strong>.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Pillar({ testId, badge, badgeTone, icon, title, desc, sources }) {
    const badgeClass =
        badgeTone === "peri"
            ? "bg-periwinkle-500/15 border-periwinkle-500/50 text-periwinkle-400"
            : "bg-amber-400/10 border-amber-400/40 text-amber-300";
    return (
        <div
            data-testid={testId}
            className="rounded-2xl border hairline border-ink-600 bg-ink-800 p-5 flex flex-col gap-3"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-1 font-mono text-[9px] tracking-widest uppercase ${badgeClass}`}
                >
                    {icon}
                    {badge}
                </span>
                <p className="font-display text-lg text-fog-100">{title}</p>
            </div>
            <p className="text-sm text-fog-300 leading-relaxed">{desc}</p>
            <ul className="space-y-1 text-xs text-fog-400 border-t hairline border-ink-600 pt-3">
                {sources.map((s, i) => (
                    <li key={i} className="flex gap-2">
                        <span className="text-periwinkle-500">•</span>
                        <span>{s}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <li className="flex gap-4">
            <span
                className="shrink-0 w-8 h-8 rounded-full border hairline border-periwinkle-500/50 bg-periwinkle-500/10 flex items-center justify-center font-display text-periwinkle-500 text-sm"
                aria-hidden="true"
            >
                {n}
            </span>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-fog-100 text-sm">{title}</p>
                <p className="text-sm text-fog-300 leading-relaxed mt-0.5">
                    {body}
                </p>
            </div>
        </li>
    );
}
